import React, { useEffect, useRef, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { useI18n } from '../utilities/i18n';
import ImageCropper from './ImageCropper';

const dataUrlToFile = async (dataUrl, fileName = 'photo.jpg') => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const base = String(fileName || 'photo').replace(/\.[^.]+$/, '') || 'photo';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
};

export default function AdminPhotoUpload({
  preview,
  error,
  onFileChange,
  disabled,
  aspect = 1,
  enableCrop = true,
  cropTitle,
}) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const retriedRef = useRef(false);
  const [broken, setBroken] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [sourceName, setSourceName] = useState('photo.jpg');

  useEffect(() => {
    setBroken(false);
    retriedRef.current = false;
  }, [preview]);

  const openPicker = () => {
    if (disabled || !inputRef.current) return;
    inputRef.current.value = '';
    inputRef.current.click();
  };

  const handleChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      onFileChange(null, t('stNeedImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onFileChange(null, t('stFileSize'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setBroken(false);
      retriedRef.current = false;
      if (enableCrop) {
        setSourceName(file.name || 'photo.jpg');
        setCropSrc(reader.result);
        return;
      }
      onFileChange(file, null, reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedDataUrl) => {
    try {
      const file = await dataUrlToFile(croppedDataUrl, sourceName);
      onFileChange(file, null, croppedDataUrl);
    } catch (_err) {
      onFileChange(null, t('epmCropError'));
    }
    setCropSrc(null);
  };

  const handleCropCancel = () => {
    setCropSrc(null);
  };

  const handleImageError = (event) => {
    const img = event?.currentTarget;
    if (!img || retriedRef.current) {
      setBroken(true);
      return;
    }
    const raw = String(img.currentSrc || img.src || '');
    if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) {
      setBroken(true);
      return;
    }
    retriedRef.current = true;
    try {
      const url = new URL(raw, window.location.origin);
      url.searchParams.set('v', String(Date.now()));
      img.src = url.toString();
    } catch (error) {
      img.src = `${raw.split('?')[0]}?v=${Date.now()}`;
    }
  };

  const showImage = Boolean(preview) && !broken;

  return (
    <>
      <div className="st-photo">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="st-photo-input"
          disabled={disabled}
          onChange={handleChange}
        />
        <button
          type="button"
          className={`st-photo-tile ${showImage ? 'has-image' : ''}`}
          disabled={disabled}
          onClick={openPicker}
          aria-label={showImage ? t('stPhotoChange') : t('stPhotoChoose')}
          title={showImage ? t('stPhotoChange') : t('stPhotoChoose')}
        >
          {showImage ? (
            <img
              src={preview}
              alt=""
              onError={handleImageError}
            />
          ) : null}
          <span className={`st-photo-overlay ${showImage ? '' : 'is-visible'}`}>
            <FiPlus />
          </span>
        </button>
        <small className="st-photo-hint">{t('stPhotoHint')}</small>
        {error ? <span className="st-field-error">{error}</span> : null}
      </div>
      {cropSrc ? (
        <ImageCropper
          image={cropSrc}
          aspect={aspect}
          title={cropTitle || t('epmCropTitle')}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      ) : null}
    </>
  );
}
